import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Typography, RadioGroup, FormControlLabel, Radio } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { ModalWindow } from 'src/component/common/analog/template/ModalWindow'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { AppForm } from 'src/component/common/analog/molecule/basic/AppForm'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import OrgLevelsSelector from 'src/feature/e-learning/component/organism/OrgLevelsSelector'
import { useB2bStore } from 'src/feature/b2b/store/b2b.store'
import { AppProgressLine } from 'src/component/common/analog/molecule/loader/AppProgressLine'
import { AppDivider } from 'src/component/common/analog/molecule/AppDivider'
import { useDashboardStore } from 'src/feature/dashboard/store/dashboard.store'
import { DownloadReportFormData } from 'src/feature/dashboard/model/dashboard-model'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'

export type DownloadReportProps = {
  show: boolean,
  levelId?: string,
  onClose: (reload: boolean) => void,
}

type FormState = {
  reportType: 'pdf' | 'excel',
}

const INITIAL_FORM_STATE: FormState = {
  reportType: 'pdf',
}

const FORM_CONTROL_LABEL_FONT_SIZE = 12

export const DownloadReport = observer((props: DownloadReportProps) => {
  const { show, levelId, onClose } = props
  const { translation } = useLocalizationStore()
  const b2bStore = useB2bStore()
  const dashboardStore = useDashboardStore()

  const [orgLevels, setOrgLevels] = useState<string[]>(levelId ? [levelId] : [])
  const { isLoading: isDownloadLoading } = dashboardStore.getGenericFieldsState('downloadReport')
  const isLoading = isDownloadLoading

  const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE)

  const handleReportTypeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFormState(prev => ({
      ...prev,
      reportType: event.target.value as 'pdf' | 'excel',
    }))
  }, [])

  const canSubmit = useMemo(() => orgLevels.length > 0, [orgLevels])

  useEffect(() => {
    b2bStore.handleLoadOrganisationLevels()
  }, [b2bStore])

  useEffect(() => {
    setOrgLevels(levelId ? [levelId] : [])
  }, [levelId])

  const downloadReport = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const reportData: DownloadReportFormData = {
      orgLevels,
      reportType: formState.reportType,
    }
    await dashboardStore.downloadReport(reportData)
    onClose(true)
  }, [orgLevels, formState.reportType, dashboardStore, onClose])

  const handleClose = useCallback(() => onClose(false), [onClose])

  return (
    <ModalWindow
      modalProps={{
        open: show,
        onClose: handleClose,
        PaperProps: {
          sx: {
            maxWidth: { xs: '90%', md: '60%', lg: '30%' },
            borderRadius: '10px',
          },
        },
      }}
      titleProps={{
        textValue: translation.pages.myTeam.downloadReport(),
      }}
      subtitleProps={{
        textValue: translation.pages.myTeam.downloadReportSubtitle(),
        sx: styles.modalSubtitle,
      }}
      headerIcon={<DownloadOutlinedIcon sx={styles.modalIcon} />}
    >
      <AppBox sx={styles.container}>
        <AppForm sx={styles.form} onSubmit={downloadReport}>
          <OrgLevelsSelector
            label={translation.pages.controlpanelUsers.inviteDialog.groupField()}
            placeholder=''
            defaultValues={orgLevels}
            onChange={setOrgLevels}
            organiationLevels={b2bStore.organisationLevels}
          />
          <AppBox flexDirection='column' gap={1}>
            <Typography sx={styles.sectionLabel}>
              {`${translation.components.postHeader.buttons.reportPost()} ${translation.pages.signUp.teamStructureType()}`}
            </Typography>
            <RadioGroup value={formState.reportType} onChange={handleReportTypeChange} row>
              <FormControlLabel value="pdf" control={<Radio size="small" />} label="PDF" sx={styles.formControlLabel} />
              <FormControlLabel value="excel" control={<Radio size="small" />} label="Excel" sx={styles.formControlLabel} />
            </RadioGroup>
          </AppBox>
          <AppDivider />
          <AppBox sx={styles.buttonContainer}>
            <AppButton
              textValue={translation.buttons.cancel()}
              fontSize={12}
              variant="light"
              onClick={handleClose}
            />
            <AppButton
              textValue={translation.buttons.confirm()}
              fontSize={12}
              variant="contained"
              disabled={isLoading || !canSubmit}
              type='submit'
            />
          </AppBox>
        </AppForm>
        <AppProgressLine isShowing={isLoading} />
      </AppBox>
    </ModalWindow>
  )
})

const styles = {
  container: {
    flexDirection: 'column',
    gap: 10,
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
  },
  formControlLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: FORM_CONTROL_LABEL_FONT_SIZE,
    },
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 400,
    color: 'text.primary',
  },
  modalIcon: {
    color: 'secondary.main',
    fontSize: 20,
  },
  modalSubtitle: {
    color: 'text.secondary',
    fontSize: 12,
    lineHeight: 1.4,
    m: 0,
  },
  buttonContainer: {
    gap: 10,
    justifyContent: 'flex-end',
    width: '100%',
  },
}
